import React, { useMemo } from 'react';
import ReactApexChart from '@/components/ReactApexChartCompat';

interface AIHistoryChartModalProps {
  selectedCardData?: {
    title?: string;
    type?: number;
  };
  chartData?: {
    categories?: string[];
    data?: number[];
  };
}

const AIHistoryChartModal: React.FC<AIHistoryChartModalProps> = ({
  selectedCardData,
  chartData,
}) => {
  const typeColors: Record<number, string> = {
    1: '#1292E8',
    2: '#009A51',
    3: '#FFC914',
    4: '#FA4E56',
  };
  const chartColor = typeColors[selectedCardData?.type ?? 0] || '#2979FF';

  const getYAxisConfig = (type?: number) => {
    const isPercentage = type === 2 || type === 3;

    return {
      min: isPercentage ? 0 : undefined,
      max: isPercentage ? 100 : undefined,
      tickAmount: isPercentage ? 5 : 3,
      labels: {
        show: true,
        formatter: (value: number): string =>
          isPercentage ? `${value}%` : `${value}`,

        style: {
          fontSize: '10px',
          colors: 'var(--text-secondary)',
        },
      },
    };
  };

  const aiHistoryChart = useMemo(
    () => ({
      options: {
        chart: {
          type: 'bar' as const,
          width: '100%',
          toolbar: {
            show: false,
          },
          animations: {
            enabled: false,
            animateGradually: { enabled: false },
            dynamicAnimation: { enabled: false },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '30%',
            borderRadius: 0,
          },
        },

        colors: [chartColor],

        dataLabels: {
          enabled: false,
        },

        xaxis: {
          categories: chartData?.categories || [],
          labels: {
            style: {
              fontSize: '10px',
              colors: 'var(--text-secondary)',
            },
          },
        },

        yaxis: getYAxisConfig(selectedCardData?.type),

        grid: {
          borderColor: 'var(--gray-300)',
        },

        responsive: [
          {
            breakpoint: 1440,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: '25%',
                },
              },
              xaxis: {
                labels: {
                  style: {
                    fontSize: '10px',
                  },
                },
              },
              yaxis: getYAxisConfig(selectedCardData?.type),
            },
          },

          {
            breakpoint: 450,
            options: {
              plotOptions: {
                bar: {
                  columnWidth: '25%',
                },
              },
              xaxis: {
                labels: {
                  style: {
                    fontSize: '8px',
                  },
                },
              },
              yaxis: getYAxisConfig(selectedCardData?.type),
            },
          },
        ],
      },

      series: [
        {
          name: selectedCardData?.title || '--',
          data: chartData?.data || [],
        },
      ],
    }),
    [chartData, selectedCardData, chartColor]
  );

  return (
    <>
      <ReactApexChart
        className='ai-history-chart'
        options={{
          ...aiHistoryChart.options,
          chart: {
            ...aiHistoryChart.options.chart,
            width: '100%',
          },
        }}
        series={aiHistoryChart.series}
        type='bar'
        height={214}
      />
    </>
  );
};

export default AIHistoryChartModal;
